'use client';

import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useCommerceSource,
  tx,
  type Order,
  type OrderLookup,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import { OrderView, OrderLookupForm } from './orderViews';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * "Where is my order?" for a shopper who has no account.
 *
 * <p>The whole point of this block is that a guest order is a first-class
 * order. Making people register to see what they bought is the single largest
 * cause of abandoned baskets, and a shop that then hides the order behind the
 * account they refused to create has taken their money and shut the door.
 *
 * <p>Nothing is fetched until the form is submitted, and the result is held in
 * component state only — never in storage, never in the URL. Someone else on
 * the same machine gets a blank form, not somebody's address.
 */
export interface OrderTrackingProps {
  title?: Localized;
  /** Explains what the shopper needs to have to hand. */
  intro?: Localized;
}

export function OrderTracking({ title, intro }: OrderTrackingProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const [order, setOrder] = useState<Order | null>(null);
  // Held with the order, in component state only, so a return can be asked for without asking again.
  const [proof, setProof] = useState<OrderLookup | undefined>(undefined);

  if (!commerce?.trackOrder) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="OrderTracking"
            adapterName="CommerceSource"
            reason={t('commerce.order.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 820 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.order.trackTitle')}
        </h1>

        {order ? (
          <>
            <button
              type="button"
              onClick={() => {
                setOrder(null);
                setProof(undefined);
              }}
              style={backStyle}
            >
              {t('commerce.order.lookUpAnother')}
            </button>
            <div style={{ marginTop: 24 }}>
              <OrderView order={order} proof={proof} onChange={setOrder} />
            </div>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.order.trackIntro')}
            </p>
            <OrderLookupForm
              onFound={(found, lookup) => {
                setOrder(found);
                setProof(lookup);
              }}
            />
          </>
        )}
      </div>
    </BlockRoot>
  );
}

const titleStyle: CSSProperties = { margin: '0 0 16px', fontSize: 'var(--font-size-3xl, 30px)' };
const leadStyle: CSSProperties = { margin: '0 0 20px' };
const backStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-text-secondary, #6b7280)',
  textDecoration: 'underline',
  cursor: 'pointer',
  font: 'inherit',
};
